<template>
  <AppChrome
    :selected-domain="selectedDomain"
    :can-change-password="canChangePassword"
    :current-user="currentUser"
    :domain-branding="selectedDomainBranding"
    :domain-options="availableDomains"
    :selectable-domain-types="[DomainType.Client, DomainType.TopLevelDomain]"
    :home-route="{ name: 'home' }"
    :content-only="contentOnly"
    :corsair-analytics-enabled="isCorsairAnalyticsEnabled"
    :notifications-sse-enabled="isNotificationsSseEnabled"
    :user-settings-enabled="isUserSettingsEnabled"
    :app-banner-enabled="isImpersonatingUser"
    :selected-domain-natural-id="selectedDomain?.naturalId"
    :hide-change-domain-button="!requiresDomainContext"
    @change-domain="onChangeDomain"
    @change-password="onChangePassword"
    @login="onLogin"
    @logout="onLogout"
  >
    <template #appBanner>
      <UserImpersonationBanner
        v-if="isImpersonatingUser"
        :display-name="currentUser?.user?.displayName"
        @end-session="onEndSession"
      />
    </template>

    <template v-if="currentUser" #app-header-middle>
      <QuickQuickSearch
        v-if="isQuickSearchKillSwitchEnabled && isQuickQuickSearchEnabled"
        ref="quickSearchRef"
        :placeholder="placeholder"
        @keydown.esc="onEscapePress"
      />
      <QuickSearch
        v-else-if="isQuickSearchKillSwitchEnabled"
        ref="quickSearchRef"
        :placeholder="placeholder"
        @keydown.esc="onEscapePress"
      />
    </template>

    <template v-if="showAiAssistant" #app-header-right>
      <AiAssistantLauncher />
    </template>

    <template #corsair>
      <NavItem
        v-if="$hasAuthority('quickQuote', 'create')"
        :to="{ name: 'quickQuote' }"
        :label="t('quickQuote.quickQuote')"
      />
      <NavItem
        v-if="$hasAuthority('order', 'read')"
        :to="{ name: 'orderList' }"
        :label="t('orders.orders')"
      />
      <NavItem
        v-if="$hasAuthority('shipment', 'read')"
        :to="{ name: 'shipmentList' }"
        :label="t('shipments.shipments')"
      />
      <NavItem
        v-if="isParcelManifestingEnabled && $hasAuthority('shipment', 'read')"
        :to="{ name: 'manifestList' }"
        :label="t('manifests.manifests', 2)"
      />
      <NavItem
        v-if="
          isShipmentBuilderEnabled && $hasAuthority('shipment', 'consolidate')
        "
        :to="{ name: 'shipOrderConsolidation' }"
        :label="t('multistop.shipmentBuilder')"
      />
      <NavItem
        v-if="isRouteBuilderEnabled && $hasAuthority('shipment', 'consolidate')"
        :to="{ name: 'routeBuilder' }"
        :label="t('routeBuilder.routeBuilder')"
      />
      <NavItem
        v-if="
          isRoutePlanListEnabled && $hasAuthority('shipment', 'consolidate')
        "
        :to="{ name: 'routePlanList' }"
        :label="t('routeBuilder.routePlans', 2)"
      />
      <NavItem
        v-if="isAiOrderEntryEnabled && $hasAuthority('order', 'create')"
        :to="{ name: 'aiOrderEntryDraftList' }"
        :label="t('aiOrderEntryDraft.drafts', 2)"
      />
      <NavItem
        v-if="
          $hasAuthority('domainPopulator', 'invoke') && isNonProdEnvironment
        "
        :to="{ name: 'domainPopulator' }"
        :label="t('domainPopulator.navLabel')"
      />
    </template>

    <template #message>
      <DashboardVersionBanner
        v-if="isDashboardVersionBannerVisible"
        :model-value="isVNextActive"
        :deprecation-date="deprecationDate"
        :mandatory-switch-date="mandatorySwitchDate"
        @update:model-value="changeVersion"
      />

      <MessageContainer
        :messages="visibleMessages"
        message-class="app-message"
        class="app-level"
      />
    </template>

    <router-view :key="selectedDomainId" />

    <DomainSwitcherModal :visible="!!targetDomainId" />

    <CarrierMessengerProvider
      v-if="carrierMessagingEnabled && selectedDomain?.id"
      :key="selectedDomain.id"
      :current-user="currentUser"
    />

    <AiAssistantProvider v-if="showAiAssistant" :key="selectedDomain?.id" />
  </AppChrome>
</template>

<script lang="ts" setup>
import { useFlag, useUnleashContext } from '@unleash/proxy-client-vue';
import axios from 'axios';
import { usePrimeVue } from 'primevue/config';
import type { Ref } from 'vue';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import {
  useDashboardVersionBanner,
  useImpersonatedUser,
  useQuickSearch,
  useToast,
} from '@rygen/rygen-ui/composables';
import { Environment } from '@rygen/rygen-ui/env/Environment';
import { EnvironmentPropertiesLoader } from '@rygen/rygen-ui/env/EnvironmentPropertiesLoader';
import environmentPropertiesStore from '@rygen/rygen-ui/env/EnvironmentPropertiesStore';
import {
  DomainReference,
  DomainType,
  IMPERSONATE_USER_KEY,
  Module,
} from '@rygen/rygen-ui/model';
import activeAuthServiceRegistry from '@rygen/rygen-ui/service/ActiveAuthServiceRegistry';

import { useKillSwitchFlag } from '@/composables';
import { getActiveAuthProvider } from '@/config/AuthProviderInstaller';
import { useCreditUsageStore } from '@/stores/CreditUsage';
import { ANONYMOUS_USERNAME, useCurrentUserStore } from '@/stores/CurrentUser';
import { useMessageStore } from '@/stores/Message';
import { useVersionStore } from '@/stores/VersionStore';
import { version as fVersion } from '@/version';

import AiAssistantProvider from '@/components/aiAssistant/AiAssistantProvider.vue';
import CarrierMessengerProvider from '@/components/carrierMessaging/CarrierMessengerProvider.vue';
import DomainSwitcherModal from '@/components/domain/DomainSwitcherModal.vue';
import QuickQuickSearch from '@/components/search/QuickQuickSearch.vue';
import QuickSearch from '@/components/search/QuickSearch.vue';
import MessageContainer from '@/components/ui/MessageContainer.vue';
import AiAssistantLauncher from '@rygen/rygen-ui/components/aiAssistant/AiAssistantLauncher.vue';
import AppChrome from '@rygen/rygen-ui/components/AppChrome.vue';
import DashboardVersionBanner from '@rygen/rygen-ui/components/dashboard/DashboardVersionBanner.vue';
import NavItem from '@rygen/rygen-ui/components/ui/NavItem.vue';
import UserImpersonationBanner from '@rygen/rygen-ui/components/user/UserImpersonationBanner.vue';

defineExpose({
  $primevue: usePrimeVue(),
});

const aiAssistantEnabled = useFlag('ai-assistant-feat-flag');
const isAiOrderEntryEnabled = useFlag(
  'corsair-ai-service-order-entry-feat-flag',
);
const carrierMessagingEnabled = useFlag('carrier-messaging-feat-flag');
const isCorsairAnalyticsEnabled = useFlag('corsairAnalytics');
const isNotificationsSseEnabled = useFlag('notifications-sse-feat-flag');
const isParcelManifestingEnabled = useFlag('parcel-manifest-feat-flag');
const isQuickQuickSearchEnabled = useFlag('quickQuickSearch');
const isQuickSearchKillSwitchEnabled = useKillSwitchFlag(
  'tms-quick-search-kill-switch',
);
const isRouteBuilderEnabled = useFlag('route-builder-feat-flag');
const isRoutePlanListEnabled = useFlag(
  'route-builder-transport-segment-feat-flag',
);
const isShipmentBuilderEnabled = useFlag('shipment-builder-feat-flag');
const isUserSettingsEnabled = useFlag('user-settings-feat-flag');
const isVersionHardRefreshEnabled = useFlag('version-hard-refresh-kill-switch');

const quickSearchRef = useTemplateRef('quickSearchRef');

const {
  applyUrlDomainContext,
  availableDomains,
  clearCurrentDomain,
  currentUser,
  getAvailableDomains,
  initializeDomainContext,
  selectedDomain,
  selectedDomainBranding,
  selectedDomainId,
  setSelectedDomain,
  targetDomainId,
} = useCurrentUserStore();
const { loadCreditUsage } = useCreditUsageStore();
const { t } = useI18n();
const {
  isVisible: isDashboardVersionBannerVisible,
  isVNextActive,
  deprecationDate,
  mandatorySwitchDate,
  changeVersion,
} = useDashboardVersionBanner();
const { isImpersonatingUser } = useImpersonatedUser();
const { visibleMessages } = useMessageStore();
const { onEscapePress, placeholder } = useQuickSearch(quickSearchRef);
const router = useRouter();
const toast = useToast();
const updateUnleashContext = useUnleashContext();
const { backendVersion: bVersion, setHardRefreshEnabled } = useVersionStore();

const canChangePassword = ref(false);

const accessToken: Ref<string | undefined> = computed(() => {
  return router.currentRoute.value.query.accessToken as string | undefined;
});

const isNonProdEnvironment = computed(() => {
  const effectiveEnvironment =
    EnvironmentPropertiesLoader.getEffectiveEnvironment(
      window.location.hostname,
    );
  return effectiveEnvironment !== Environment.PROD;
});

const contentOnly = computed(
  () => !!router.currentRoute.value.meta.contentOnly,
);

const requiresDomainContext = computed(
  () => !!router.currentRoute.value.meta.requiresDomainContext,
);

const showAiAssistant = computed(
  () =>
    aiAssistantEnabled.value &&
    getActiveAuthProvider() === 'keycloak' &&
    !!selectedDomain.value?.id,
);

const onEndSession = async () => {
  localStorage.removeItem(IMPERSONATE_USER_KEY);
  router.go(0);
};

const onChangeDomain = async (domain: DomainReference) => {
  if (router.currentRoute.value.meta.redirectOnDomainChange) {
    await router.replace({ name: 'home' });
  }
  setSelectedDomain(domain);
};

const onLogout = () => {
  activeAuthServiceRegistry.getInstance().logout();
  clearCurrentDomain();
};

const onLogin = () => {
  activeAuthServiceRegistry.getInstance().login();
};

const onChangePassword = () => {
  activeAuthServiceRegistry.getInstance().changePassword();
};

const unleashContext = computed(() => {
  const defaultUserId = accessToken.value ? ANONYMOUS_USERNAME : '';

  return {
    userId: currentUser.value?.user?.username ?? defaultUserId,
    appName: environmentPropertiesStore.getValue('UNLEASH_ENVIRONMENT_NAME'),
    properties: {
      domainNaturalId: selectedDomain.value?.naturalId ?? '',
      domainRealmNaturalId:
        selectedDomain.value?.realm?.leaderDomain?.naturalId ?? '',
      environmentName: environmentPropertiesStore.getValue('ENVIRONMENT_NAME'),
    },
  };
});

watch(
  unleashContext,
  (newVal) => {
    if (updateUnleashContext) {
      updateUnleashContext(newVal);
    }
  },
  { immediate: true, deep: true },
);

watch(isVersionHardRefreshEnabled, setHardRefreshEnabled, {
  immediate: true,
});

// Prints versions to console the first time backend is updated,
// then removes watcher on subsequent API response (also will print on each refresh)
watch(
  bVersion,
  (newVal: string) => {
    if (newVal) {
      console.info(`Frontend v${fVersion} | Backend v${newVal}`);
    }
  },
  { once: true },
);

onMounted(async () => {
  const authServiceInstance = activeAuthServiceRegistry.getInstance();

  await authServiceInstance.handleRedirectPromise();
  if (authServiceInstance.isAuthenticated()) {
    try {
      await getAvailableDomains();
      const appliedUrlContext = await applyUrlDomainContext(Module.TMS);
      if (!appliedUrlContext) {
        await initializeDomainContext();
      }
      await loadCreditUsage();
    } catch (e: any) {
      if (
        axios.isAxiosError(e) &&
        (e.response?.status === 401 || e.response?.status === 403)
      ) {
        router.replace({ name: '401' });
      } else {
        toast.addError({
          //TODO: add a proper error page
          detail: 'Something went wrong',
          error: e,
        });
      }
    }

    canChangePassword.value =
      authServiceInstance.isChangePasswordSupportedForCurrentAccount() &&
      !isImpersonatingUser.value;
  }
});
</script>

<style>
:root {
  /* .content height - .content-header height - .content-header bottom margin */
  --content-container-height: calc(100% - 42px - var(--sp-6));
}

#app {
  --map-translation: 100px;
  --map-expanded-translation: 350px;

  &.with-map {
    .app-chrome {
      main {
        padding-bottom: calc(
          var(--map-translation) + var(--app-chrome-main-side-padding)
        );

        .content-header {
          pointer-events: all;

          .header-icon-background {
            opacity: 0.4;
          }
        }

        .content-container {
          transform: translateY(var(--map-translation));
        }

        .content-container {
          pointer-events: all;
        }
      }
    }

    &.map-expanded {
      .app-chrome {
        main {
          padding-bottom: calc(
            var(--map-expanded-translation) +
              var(--app-chrome-main-side-padding)
          );

          .content-container {
            transform: translateY(var(--map-expanded-translation));
          }
        }
      }

      .content {
        pointer-events: none;
      }
    }

    .content-only {
      .app-main-container {
        overflow: unset;

        main {
          padding-bottom: calc(var(--map-translation) + var(--sp-11));

          .map {
            top: 0;
            left: 0;
          }

          .content-container {
            transform: translateY(var(--map-translation));
          }
        }
      }
    }
  }

  &:has(.router-builder-pop-out-map) {
    --app-chrome-main-padding: 0;
  }

  .message-container.app-level {
    gap: 0;

    .app-message {
      --p-message-border-radius: 0;
      --p-message-border-width: 0;
      --p-message-content-gap: var(--sp-1);
      --p-message-content-padding: var(--sp-2);
      --p-avatar-height: 21px;
      --p-avatar-width: 21px;

      & + .app-message {
        outline: 1px solid;
      }

      height: var(--app-message-height);

      .p-message-content {
        justify-content: center;
        height: 100%;
      }
    }

    & + main .content.no-scroll {
      height: calc(
        100vh - var(--app-header-height) -
          var(--cumulative-app-messages-height) -
          var(--app-chrome-main-top-padding) -
          var(--app-chrome-main-side-padding)
      );
    }
  }

  .content {
    /* TODO: Move to Rygen UI */

    &.no-scroll {
      /*
        Force the height to be the maximum space available after all the chrome
        Note: this calc is overriden above when there are message banners
        displayed.

        100vh - headerHeight - main's top padding - main's bottom padding
        */
      height: calc(
        100vh - var(--app-header-height) - var(--app-chrome-main-top-padding) -
          var(--app-chrome-main-side-padding)
      );

      .content-container {
        height: var(--content-container-height);

        .content-container-top-row {
          height: 100%;
        }
      }
    }

    .content-container {
      transition: transform 300ms ease-out;
    }

    .content-toolbar {
      .p-inputnumber-stacked {
        /* Fix InputNumber positioning bug */
        inset-inline-end: 0;
      }
    }
  }
}
</style>
